import React, { useEffect, useMemo, useState } from "react";
import { getUserFromToken } from "../../../../utils/functions.ts";
import DigitalScouting from "../ScoutReports/digitalScout.component.jsx";

const OverviewReports = ({ FBXID }) => {
    const playerId = useMemo(() => {
        const n = Number(FBXID);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [FBXID]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);

    const clean = (v) => {
        if (v === undefined || v === null) return "";
        const s = String(v).trim();
        if (!s) return "";
        const low = s.toLowerCase();
        if (low === "null" || low === "undefined" || low === "n/a" || low === "na") return "";
        if (s === "0000-00-00" || s === "1950-01-01") return "";
        return s;
    };

    const isMeaningfulText = (v) => {
        const s = clean(v);
        if (!s) return false;
        const t = String(s).trim();
        if (!t) return false;
        const low = t.toLowerCase();
        if (t === "-" || t === "0") return false;
        if (low === "null" || low === "undefined" || low === "n/a" || low === "na") return false;
        return true;
    };

    const parsePercentValue = (raw) => {
        if (raw === undefined || raw === null) return null;
        const str = String(raw).trim();
        if (!str) return null;
        if (str === "-" || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;

        const match = str.match(/(\d+(\.\d+)?)/);
        if (!match) return null;

        const num = parseFloat(match[1]);
        if (Number.isNaN(num)) return null;
        return num;
    };

    const hasDigitalMark = (digitalBlocks) => {
        if (!Array.isArray(digitalBlocks) || digitalBlocks.length === 0) return false;

        for (const b of digitalBlocks) {
            const hv = parsePercentValue(b?.headerValue);
            if (hv !== null && hv > 0) return true;

            const subVals = Array.isArray(b?.subVals) ? b.subVals : [];
            for (const sv of subVals) {
                const n = parsePercentValue(sv);
                if (n !== null && n > 0) return true;
            }
        }
        return false;
    };

    const pickScoutId = (token) => {
        return (
            token?.UserId ??
            token?.userId ??
            token?.userid ??
            token?.ID ??
            token?.id ??
            token?.scoutnum ??
            token?.scoutNum ??
            token?.scout ??
            ""
        );
    };

    const normalizeRow = (r) => {
        const id =
            r?.id ??
            r?.Dr_SR_Num ??
            r?.DR_SR_Num ??
            `${r?.FBXID || ""}-${r?.REPORT_DATE || r?.creationDate || Math.random()}`;

        const reportDate = clean(r?.REPORT_DATE) || clean(r?.reportDate) || clean(r?.creationDate) || "-";

        const scout =
            clean(r?.SCOUT) ||
            clean(r?.Scout) ||
            clean(r?.scoutName) ||
            clean(r?.scout) ||
            clean(r?.scoutnum) ||
            "-";

        const exposure = clean(r?.exposure) || clean(r?.P_GM_1) || "-";
        const pos = clean(r?.COL_POS1) || "-";
        const draftYear = clean(r?.DRAFT_YEAR) || "-";

        const gradeCode = clean(r?.gradeCode) || "-";
        const finalVal = clean(r?.FINAL) || "-";
        const finalCalc = clean(r?.Final_Calculated) || "-";

        const isPrivate = clean(r?.isPrivate) || "N";
        const visibleTo = clean(r?.VisibleTo) || "-";

        const summary = isMeaningfulText(r?.SUMMARY) ? clean(r?.SUMMARY) : "";
        const strength = isMeaningfulText(r?.M_STRENGTH) ? clean(r?.M_STRENGTH) : "";
        const weakness = isMeaningfulText(r?.M_WEAKNESS) ? clean(r?.M_WEAKNESS) : "";

        const digital2025 = Array.isArray(r?.digital2025) ? r.digital2025 : [];

        return {
            id,
            reportDate,
            scout,
            exposure,
            pos,
            draftYear,
            gradeCode,
            finalVal,
            finalCalc,
            isPrivate,
            visibleTo,
            summary,
            strength,
            weakness,
            digital2025,
            raw: r,
        };
    };

    useEffect(() => {
        const controller = new AbortController();

        const fetchReports = async () => {
            setRows([]);
            setErrMsg("");
            setCurrentIndex(0);

            if (!playerId) return;

            setLoading(true);

            const token = await getUserFromToken().catch(() => null);

            if (!token?.team) {
                setLoading(false);
                setErrMsg("Missing user/team token.");
                return;
            }

            const payload = {
                scriptName: "getReports",
                ID: playerId,
                team: token.team,
                scout: pickScoutId(token),
            };

            try {
                const res = await fetch("https://www.fbxchange.com/ReactPHP/scoutreports.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    signal: controller.signal,
                    body: JSON.stringify(payload),
                });

                const raw = await res.text();

                let data;
                try {
                    data = JSON.parse(raw);
                } catch (e) {
                    setErrMsg("Backend did not return JSON.");
                    setLoading(false);
                    return;
                }

                if (!data?.success) {
                    setErrMsg(data?.error || "Failed to load reports.");
                    setLoading(false);
                    return;
                }

                const reportPack = data?.data || {};
                const arr = Array.isArray(reportPack?.reports) ? reportPack.reports : [];
                const normalized = arr.map(normalizeRow);

                setRows(normalized);
                setCurrentIndex(0);
                setLoading(false);
            } catch (e) {
                if (e?.name === "AbortError") return;
                setErrMsg("Network error loading reports.");
                setLoading(false);
            }
        };

        fetchReports();

        return () => controller.abort();
    }, [playerId]);

    useEffect(() => {
        if (!rows.length) {
            setCurrentIndex(0);
            return;
        }
        if (currentIndex > rows.length - 1) {
            setCurrentIndex(rows.length - 1);
        }
    }, [rows, currentIndex]);

    const currentReport = rows[currentIndex] || null;
    const canGoBack = currentIndex > 0;
    const canGoNext = currentIndex < rows.length - 1;

    const goBack = () => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    const goNext = () => setCurrentIndex((prev) => (prev < rows.length - 1 ? prev + 1 : prev));

    const showSummary = isMeaningfulText(currentReport?.summary);
    const showStrength = isMeaningfulText(currentReport?.strength);
    const showWeakness = isMeaningfulText(currentReport?.weakness);
    const showDigital = hasDigitalMark(currentReport?.digital2025);

    const showAnything = showSummary || showStrength || showWeakness || showDigital;

    return (
        <div className="container-fluid p-0 h-100">
            <div className="row g-0 h-100">
                <div className="col-48 h-100 d-flex flex-column">
                    <div
                        className="row g-1 bg-navy text-lemon fs-7 py-2 px-2 align-items-center"
                        style={{ position: "sticky", top: 0, zIndex: 25 }}
                    >
                        <div className="col-20">
                            <strong>Player Reports</strong>
                        </div>

                        <div className="col-12 text-center">
                            {!loading && !errMsg && rows.length > 0 ? (
                                <span>
                                    {currentIndex + 1} / {rows.length}
                                </span>
                            ) : (
                                <span>-</span>
                            )}
                        </div>

                        <div className="col-16 text-end">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light me-1"
                                onClick={goBack}
                                disabled={!canGoBack || loading || !!errMsg || rows.length === 0}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light"
                                onClick={goNext}
                                disabled={!canGoNext || loading || !!errMsg || rows.length === 0}
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    <div className="p-2 flex-grow-1" style={{ minHeight: 0 }}>
                        {loading ? (
                            <div className="text-muted fs-7">
                                <em>Loading reports...</em>
                            </div>
                        ) : errMsg ? (
                            <div className="alert alert-warning py-2 mb-0 fs-7">{errMsg}</div>
                        ) : rows.length === 0 ? (
                            <div className="text-muted small">
                                <em>No reports found.</em>
                            </div>
	                        ) : (
	                            <div className="bg-white border rounded shadow-sm h-100 d-flex flex-column" style={{ minHeight: 0 }}>
	                                <div className="px-2 pb-3 d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
	                                    <div
	                                        className="mt-2 border w-100 border-rounded"
	                                        style={{
	                                            flex: 1,
	                                            minHeight: 0,
                                            overflowY: "auto",
                                            overflowX: "hidden",
	                                        }}
	                                    >
	                                        <div className="w-100 px-2 py-2 border-bottom border-1 border-navy">
	                                            <div className="row g-2 align-items-end">
	                                                <div className="col-6">
	                                                    <label className="form-label mb-1 small text-muted">Grade</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.finalVal || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-6">
	                                                    <label className="form-label mb-1 small text-muted">Calc. Grade</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.finalCalc || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-6">
	                                                    <label className="form-label mb-1 small text-muted">Codes</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.gradeCode || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-6">
	                                                    <label className="form-label mb-1 small text-muted">Report Pos</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.pos || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-12">
	                                                    <label className="form-label mb-1 small text-muted">Scout</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.scout || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-6">
	                                                    <label className="form-label mb-1 small text-muted">Report Date</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.reportDate || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-6">
	                                                    <label className="form-label mb-1 small text-muted">Visible To</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.visibleTo || ""}
	                                                        disabled
	                                                    />
	                                                </div>
	                                            </div>
	                                        </div>

	                                        <div className="border-bottom border-light px-3 py-2">
	                                            <div className="row g-2">
	                                                <div className="col-24">
	                                                    <label className="form-label mb-1 small text-muted">Exposure</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.exposure || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-12">
	                                                    <label className="form-label mb-1 small text-muted">Draft Year</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.draftYear || ""}
	                                                        disabled
	                                                    />
	                                                </div>

	                                                <div className="col-12">
	                                                    <label className="form-label mb-1 small text-muted">Privacy</label>
	                                                    <input
	                                                        className="form-control form-control-sm"
	                                                        value={currentReport?.isPrivate === "Y" ? "Private" : "Public"}
	                                                        disabled
	                                                    />
	                                                </div>
	                                            </div>
	                                        </div>

	                                        <div className="row g-2 p-2">
                                            {!showAnything ? (
                                                <div className="col-48 text-muted small">
                                                    <em>No Summary / Strength / Weakness / Digital data for this report.</em>
                                                </div>
                                            ) : (
                                                <>
                                                    {showSummary && (
                                                        <div className="col-48">
                                                            <div className="mb-2">
                                                                <div className="text-uppercase" style={{ lineHeight: "20px" }}>
                                                                    Summary
                                                                </div>
                                                            </div>
                                                            <textarea
                                                                rows={5}
                                                                className="form-control"
                                                                value={currentReport?.summary || ""}
                                                                disabled
                                                            />
                                                        </div>
                                                    )}

                                                    {(showStrength || showWeakness) && (
                                                        <div className="col-48">
                                                            <div className="d-flex gap-2 mb-2">
                                                                {showStrength && (
                                                                    <div className="col-24">
                                                                        <label className="form-label">Positive</label>
                                                                        <textarea
                                                                            rows={4}
                                                                            className="form-control"
                                                                            value={currentReport?.strength || ""}
                                                                            disabled
                                                                        />
                                                                    </div>
                                                                )}

                                                                {showWeakness && (
                                                                    <div className="col-24">
                                                                        <label className="form-label">Negative</label>
                                                                        <textarea
                                                                            rows={4}
                                                                            className="form-control"
                                                                            value={currentReport?.weakness || ""}
                                                                            disabled
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {showDigital && (
                                                        <div className="col-48">
                                                            <div className="mb-2">
                                                                <div className="text-uppercase" style={{ lineHeight: "20px" }}>
                                                                    Digital
                                                                </div>
                                                            </div>
                                                            <div className="border rounded p-2">
                                                                <DigitalScouting
                                                                    reportData={currentReport?.digital2025}
                                                                    reportId={currentReport?.id}
                                                                    canEdit={false}
                                                                    onDigitalChange={null}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OverviewReports;
